import React, { useRef, useState } from "react";
import { Button, message } from "antd";
import * as XLSX from "xlsx";
import { fetchEstimations, fetchStats } from "../../utils/helpers";
import { useDispatch } from "react-redux";
import { POST } from "../../utils/apis";
import { secureApi } from "../../Config/api";
import { translations } from "../../utils/constants";

const ExcelUploadButton = (props) => {
    const { vehicleId, language, user } = props
    const dispatch = useDispatch();
    const inputRef = useRef(null);
    const [loading, setLoading] = useState(false);

    const ALLOWED_EXTS = [".xlsx", ".xls"];
    const ALLOWED_MIME = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
    ];

    const handleClick = () => {
        inputRef.current?.click();
    };


    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setLoading(true);

        const lowerName = file.name.toLowerCase();
        const hasValidExt = ALLOWED_EXTS.some((ext) => lowerName.endsWith(ext));
        const hasValidMime = ALLOWED_MIME.includes(file.type);

        if (!hasValidExt || !hasValidMime) {
            message.error("Please upload a valid Excel file (.xlsx or .xls).");
            e.target.value = "";
            setLoading(false);
            return;
        }

        const normalizeKey = (k = "") =>
            String(k).trim().toLowerCase().replace(/\s+/g, "_");

        const toNumber = (val) => {
            if (val === null || val === undefined || val === "") return null;
            if (typeof val === "number" && isFinite(val)) return val;
            const cleaned = String(val).replace(/[^\d.,-]/g, "").replace(/,/g, "");
            const num = Number(cleaned);
            return isFinite(num) ? num : null;
        };

        try {
            const arrayBuffer = await file.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: "array" });
            const firstSheetName = workbook.SheetNames[0];
            const ws = workbook.Sheets[firstSheetName];

            const rows = XLSX.utils.sheet_to_json(ws, { defval: null });

            if (!rows.length) {
                message.warning("The Excel sheet is empty.");
                return;
            }

            const headerKeys = Object.keys(rows[0] || {});
            const normMap = headerKeys.reduce((acc, k) => {
                acc[normalizeKey(k)] = k;
                return acc;
            }, {});

            const nameCandidates = ["name", "row_name", "product", "item", "title"];
            const priceCandidates = ["price", "unit_price", "amount", "cost", "rate"];

            const nameKey =
                nameCandidates.map((x) => normMap[x]).find(Boolean) || headerKeys[0];
            const priceKey =
                priceCandidates.map((x) => normMap[x]).find(Boolean) || headerKeys[1];

            if (!nameKey || !priceKey) {
                message.error("Could not detect name/price columns in the sheet.");
                return;
            }

            const mappedRows = rows
                .map((r) => {
                    const row_name = r[nameKey];
                    const unit_price = toNumber(r[priceKey]);
                    return {
                        row_name: row_name != null ? String(row_name).trim() : "",
                        unit_price,
                    };
                })
                .filter((r) => r.row_name && r.unit_price !== null);
            if (!mappedRows.length) {
                message.error("No valid rows found (check Name/Price columns).");
                return;
            }

            const payload = {
                vehicle_id: vehicleId,
                rows: mappedRows,
            };

            const res = await secureApi.post(POST.ADD_BULK_UNITS, payload)
            if (res?.success) {
                message.success(translations[language]?.unitAdded)
                fetchStats({ dispatch, user, vehicleId })
                fetchEstimations({ user, vehicleId, dispatch })
            } else {
                message.error(res?.message || translations[language]?.unitAddError)
            }
        } catch (err) {
            console.error("Failed to parse Excel:", err);
            message.error("Failed to read the Excel file.");
        } finally {
            e.target.value = "";
            setLoading(false);
        }
    };


    return (
        <div>
            <Button onClick={handleClick} loading={loading} disabled={loading}>
                {loading ? translations[language]?.process : translations[language]?.uploadExcel}

            </Button>
            <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
        </div>
    );
};

export default ExcelUploadButton;
